/**
 * Ring buffer of recent console output for Report Issue diagnostics (T-002).
 */

import { redactConsoleLogLine } from './consoleRedaction';

const MAX_LINES = 200;
const lines: string[] = [];
let writeIndex = 0;
let count = 0;
let installed = false;

function pushLine(rawLine: string): void {
    const line = redactConsoleLogLine(rawLine);
    if (count < MAX_LINES) {
        lines.push(line);
        count += 1;
        writeIndex = count % MAX_LINES;
    } else {
        lines[writeIndex] = line;
        writeIndex = (writeIndex + 1) % MAX_LINES;
    }
}

function formatConsoleArgs(args: unknown[]): string {
    return args
        .map(arg => {
            if (typeof arg === 'string') {
                return arg;
            }
            try {
                return JSON.stringify(arg);
            } catch {
                return String(arg);
            }
        })
        .join(' ');
}

function wrapConsoleMethod(level: 'log' | 'warn' | 'error' | 'info'): void {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
        pushLine(`[${level}] ${formatConsoleArgs(args)}`);
        original(...args);
    };
}

/** Returns recent console lines in chronological order (already redacted). */
export function getConsoleLogs(): string[] {
    // count stops at MAX_LINES once the buffer is full; use writeIndex once full
    // so wrapped contents remain chronological.
    if (count < MAX_LINES) {
        return [...lines];
    }
    return [...lines.slice(writeIndex), ...lines.slice(0, writeIndex)];
}

/** Patch console and global error handlers; safe to call once at boot. */
export function installConsoleRingBuffer(): void {
    if (installed || typeof window === 'undefined') {
        return;
    }
    installed = true;

    wrapConsoleMethod('log');
    wrapConsoleMethod('warn');
    wrapConsoleMethod('error');
    wrapConsoleMethod('info');

    window.addEventListener('error', event => {
        const message = event.message || 'Unknown error';
        const location = event.filename ? `${event.filename}:${event.lineno ?? 0}:${event.colno ?? 0}` : 'unknown';
        pushLine(`[error] ${message} (${location})`);
    });

    window.addEventListener('unhandledrejection', event => {
        const reason =
            event.reason instanceof Error
                ? event.reason.message
                : typeof event.reason === 'string'
                  ? event.reason
                  : JSON.stringify(event.reason);
        pushLine(`[unhandledrejection] ${reason}`);
    });
}
